import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ColorScheme = 'light' | 'dark';
/** What the user *asked* for; `auto` follows the OS `prefers-color-scheme`. */
export type ThemePreference = 'light' | 'dark' | 'auto';
export type TableDensity = 'compact' | 'comfortable';

/*
 * `AppMode` and `appMode` were retired here for vendor#137.
 *
 * They encoded a product shape that no longer exists: two phone experiences —
 * `management` (the responsive app) and `show` (the `/m/*` shell) — with a
 * persisted toggle choosing between them, and `DesktopIndex` redirecting a
 * phone into `/m/home` when the stored value said `show`.
 *
 * The product owner retired that split on 2026-08-03 (ratified 2026-08-04):
 * **one responsive authenticated app on one route set.** Mobile-first is a
 * property of each screen, not a parallel tree, and show mode becomes a
 * subpage within the show rather than a persisted mode. So the toggle has
 * nothing left to toggle between.
 *
 * The persisted key is handled rather than merely dropped — see `migrate`
 * below. Every device that ever ran this app has `"appMode"` in localStorage,
 * and a third of them have it set to `"show"`.
 */

/**
 * Path the desktop shell lands on after sign-in / when visiting `/`. Kept as a
 * small allow-list (rather than a free string) so a stale persisted value can
 * never point at a route that no longer exists. `/` is the dashboard.
 *
 * **Corrected 2026-08-04 (#504).** The list carried `/alerts` — a dropped
 * feature with no route in `router.tsx` — so the allow-list did the opposite of
 * what its own docblock promised. Choosing "Alerts" on Settings → Appearance
 * made `DesktopIndex` redirect `/` to `/alerts`, which falls through to the
 * router's `path: '*'` catch-all, so the app's entry point rendered `NotFound`
 * on every visit with no way back except editing the setting again.
 *
 * `/find` is deliberately kept: it is a live route that redirects to
 * `/inventory` (`FindRedirect`), so it resolves.
 */
export const LANDING_PATHS = ['/', '/find', '/inventory', '/acquisitions'] as const;
export type LandingPath = (typeof LANDING_PATHS)[number];

/**
 * Narrows an arbitrary persisted value to a {@link LandingPath}.
 *
 * The allow-list is the guarantee, but it only holds at *write* time — a value
 * persisted to `localStorage` before an entry was removed survives the removal
 * (there is no `migrate` on this store, by design: every other field is a plain
 * enum that only ever gains members). Without this the `/alerts` fix above
 * would leave anyone who had already chosen it stuck on the same 404.
 */
export function isLandingPath(value: unknown): value is LandingPath {
  return typeof value === 'string' && (LANDING_PATHS as readonly string[]).includes(value);
}

/**
 * The containers-hub shelf's five sorts (`docs/redesign-rulings.md` §2, §4A;
 * `mtb-design-system/templates/vendor-containers/home-a.js` `SORTS`) — Room ·
 * Value · Sitting · Touched · A–Z. Kept here (device-local UI preference,
 * not server state) so `/containers`'s sort choice survives a reload per
 * issue #318's acceptance criterion, the same way `navCollapsed` and
 * `tableDensity` do.
 */
export const CONTAINER_SHELF_SORTS = ['room', 'value', 'stale', 'recent', 'name'] as const;
export type ContainerShelfSort = (typeof CONTAINER_SHELF_SORTS)[number];

/** Resolve a {@link ThemePreference} to a concrete scheme, reading the OS only for `auto`. */
export function systemColorScheme(): ColorScheme {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * The OS `prefers-reduced-motion` setting, read once as the store's initial
 * value (notes.md §11.6: "Reduced motion should read the OS setting and
 * default from it, rather than shipping on and waiting to be turned off").
 * `persist`'s rehydration overwrites this with any value already saved to
 * localStorage, so this only decides the FIRST-EVER default, never overrides
 * an explicit choice the operator already made on the Appearance page.
 */
export function systemPrefersReducedMotion(): boolean {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  return false;
}

function resolvePreference(pref: ThemePreference): ColorScheme {
  return pref === 'auto' ? systemColorScheme() : pref;
}

interface UiState {
  /**
   * The *effective* scheme applied to the app (what `MantineProvider`'s
   * `forceColorScheme` reads). Always concrete light/dark — never `auto`. When
   * `themePreference` is `auto` this is kept in sync with the OS by an effect
   * in `main.tsx`.
   */
  colorScheme: ColorScheme;
  /**
   * A shell-scoped scheme that outranks {@link colorScheme} while it is set.
   *
   * The kiosk is the only writer (vendor#388). Its scheme is the *dealer's*
   * choice, stored server-side as `kiosk_display_settings.theme` and read by
   * whoever is looking at the tablet — it is not the operator's device
   * preference, so it must not be written into `colorScheme` and must not
   * persist. `KioskLayout` sets it on mount and clears it on unmount.
   *
   * It has to reach `<html>` rather than the kiosk subtree, because Mantine 9
   * emits its scheme-dependent component rules as *descendant* selectors
   * (`[data-mantine-color-scheme='dark'] .m_xxx`). A nested `data-mantine-color-scheme`
   * would leave every kiosk component matching both the ancestor's rule and
   * its own at equal specificity, so source order in Mantine's stylesheet
   * would decide the colour — per component, unpredictably. One attribute on
   * one root is the only place the cascade is unambiguous.
   *
   * Deliberately NOT persisted (see `partialize` below): it describes a shell
   * that is mounted right now, and a copy surviving in localStorage would
   * repaint the authenticated app on the next load.
   */
  schemeOverride: ColorScheme | null;
  /** The user's chosen preference, including `auto`. Surfaced on the Appearance page. */
  themePreference: ThemePreference;
  navCollapsed: boolean;
  tableDensity: TableDensity;
  /** When true, the app suppresses non-essential animations/transitions. */
  reduceMotion: boolean;
  /** Play a short confirmation tone on every successful barcode/QR decode. */
  scanSound: boolean;
  /** Buzz (`navigator.vibrate`) on every successful barcode/QR decode — useful
   * when the room is too loud for the scan tone to be heard. */
  hapticsOnScan: boolean;
  /** Warn (native beforeunload) before leaving a page that has unsaved form edits. */
  warnUnsaved: boolean;
  /** Where the desktop shell sends you from `/`. */
  defaultLandingPath: LandingPath;
  /** The containers hub's (`/containers`) currently-selected sort. */
  containersSort: ContainerShelfSort;
  /**
   * The game the catalog search opens on — a tcgtracking category id as a
   * string ('3' Pokémon, '85' Pokémon JP, '68' One Piece, '89' Riftbound).
   *
   * **Per device, deliberately.** A dealer who sells one game picks it once,
   * and this is a convenience rather than an account fact — putting it in
   * `member_settings` would mean a migration and a round trip to answer a
   * question the device can answer instantly. If it ever needs to follow a
   * dealer between their phone and their desk, that is the moment to move it.
   */
  defaultGame: string;

  setColorScheme: (scheme: ColorScheme) => void;
  /** Pin the applied scheme to a shell's own choice, or `null` to release it. */
  setSchemeOverride: (scheme: ColorScheme | null) => void;
  toggleColorScheme: () => void;
  setThemePreference: (pref: ThemePreference) => void;
  setNavCollapsed: (collapsed: boolean) => void;
  toggleNav: () => void;
  setTableDensity: (density: TableDensity) => void;
  setDefaultGame: (game: string) => void;
  setReduceMotion: (reduce: boolean) => void;
  setScanSound: (on: boolean) => void;
  setHapticsOnScan: (on: boolean) => void;
  setWarnUnsaved: (on: boolean) => void;
  setDefaultLandingPath: (path: LandingPath) => void;
  setContainersSort: (sort: ContainerShelfSort) => void;
}

/** Where this store persists. Renamed from `mtb-storefront-ui` for vendor#137. */
export const UI_STORAGE_KEY = 'mtb-vendor-ui';
/** The pre-#137 name. Read as a fallback, never written, never deleted. */
export const LEGACY_UI_STORAGE_KEY = 'mtb-storefront-ui';

/**
 * `localStorage`, except a read that misses under the new key falls back to the
 * old one.
 *
 * **This is deliberately NOT zustand's `migrate` hook, which cannot do it.**
 * `migrate` runs only when `getItem` returned something whose stored `version`
 * differs from the current one. A device that has never written under the new
 * name has nothing stored there at all, so `getItem` returns `null`, zustand
 * skips rehydration entirely, and `migrate` is never called. The fallback has
 * to happen one layer lower — in the read itself. (Established the hard way:
 * the first version of this change used `migrate` and its test failed.)
 *
 * **The old key is never written and never deleted.** Writes go to the new name
 * only, so the legacy blob freezes at whatever it last held; and leaving it
 * means a cached older bundle, a second tab, or a rollback still finds the
 * preferences it expects. It is a few hundred bytes.
 *
 * **The fallback is not one-shot, and that is not a bug.** Zustand persists on
 * state CHANGE, not on rehydrate, so the new key stays empty until the operator
 * next touches a preference — and until then every load reads the legacy blob
 * again. Forcing a write on boot to make it "once" would mean every device
 * writing on every load forever, to save one `getItem` that already missed.
 */
const legacyReadThroughStorage: Storage = {
  get length() {
    return localStorage.length;
  },
  key: (i) => localStorage.key(i),
  clear: () => localStorage.clear(),
  removeItem: (k) => localStorage.removeItem(k),
  setItem: (k, v) => localStorage.setItem(k, v),
  getItem: (k) =>
    k === UI_STORAGE_KEY
      ? (localStorage.getItem(k) ?? localStorage.getItem(LEGACY_UI_STORAGE_KEY))
      : localStorage.getItem(k),
};

/**
 * UI-preferences store: color scheme, sidebar-collapsed state, table density,
 * and the device-local experience toggles surfaced on the Appearance settings
 * page (reduce-motion, scan sound, unsaved-changes guard, default landing).
 * Persisted to localStorage so preferences survive reloads.
 *
 * Kept separate from `useSession` because these are device-local UX
 * preferences, not server-backed identity. Both stores can be read
 * independently by any shell.
 *
 * Theme model: `themePreference` is the source of truth for what the user
 * chose (`light | dark | auto`); `colorScheme` is the concrete scheme actually
 * applied. The top-bar sun/moon quick-toggle and the Appearance page both keep
 * the two in lock-step via the setters here, and `main.tsx` re-resolves
 * `auto` against the OS media query on change.
 */
export const useUi = create<UiState>()(
  persist(
    (set) => ({
      colorScheme: 'light',
      schemeOverride: null,
      themePreference: 'light',
      navCollapsed: false,
      tableDensity: 'comfortable',
      reduceMotion: systemPrefersReducedMotion(),
      scanSound: false,
      hapticsOnScan: false,
      warnUnsaved: true,
      defaultLandingPath: '/',
      containersSort: 'room',
      defaultGame: '3',

      setColorScheme: (scheme) => set({ colorScheme: scheme }),
      setSchemeOverride: (scheme) => set({ schemeOverride: scheme }),
      toggleColorScheme: () =>
        set((s) => {
          const next: ColorScheme = s.colorScheme === 'light' ? 'dark' : 'light';
          // A manual flip is an explicit choice, so it pins the preference too —
          // otherwise an `auto` user could click the toggle and have the OS
          // immediately overrule them on the next media-query change.
          return { colorScheme: next, themePreference: next };
        }),
      setThemePreference: (pref) =>
        set({ themePreference: pref, colorScheme: resolvePreference(pref) }),
      setNavCollapsed: (collapsed) => set({ navCollapsed: collapsed }),
      toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
      setTableDensity: (density) => set({ tableDensity: density }),
      setReduceMotion: (reduce) => set({ reduceMotion: reduce }),
      setScanSound: (on) => set({ scanSound: on }),
      setHapticsOnScan: (on) => set({ hapticsOnScan: on }),
      setWarnUnsaved: (on) => set({ warnUnsaved: on }),
      setDefaultLandingPath: (path) => set({ defaultLandingPath: path }),
      setContainersSort: (sort) => set({ containersSort: sort }),
      setDefaultGame: (game) => set({ defaultGame: game }),
    }),
    {
      /**
       * Renamed from `mtb-storefront-ui` for vendor#137. The old name is a fork
       * artifact — this app has not been storefront since Phase 4 — and #137
       * asked for the rename to be taken "at the opportunity" of retiring
       * `appMode`, since both touch the same stored object.
       *
       * The rename is read-through, not destructive — see
       * {@link legacyReadThroughStorage}. Simply changing the name would
       * silently reset every operator's colour scheme, table density, scan
       * sound, haptics and landing path: device preferences they chose and
       * expect back, and which nothing on screen would explain the loss of.
       */
      name: UI_STORAGE_KEY,
      storage: createJSONStorage(() => legacyReadThroughStorage),
      /**
       * Everything here is a device preference the operator chose and expects
       * back — except {@link UiState.schemeOverride}, which describes a shell
       * that is mounted right now. Persisting it would repaint the
       * authenticated app in the kiosk's colours on the next load, with no
       * control anywhere that explains why (vendor#388).
       */
      partialize: ({ schemeOverride: _schemeOverride, ...rest }) => rest,
      /**
       * Rehydration guard for {@link LANDING_PATHS} (#504). A device that
       * persisted a landing path we have since removed — `/alerts` is the case
       * that shipped — would otherwise keep redirecting `/` at a route that no
       * longer exists. Anything not currently in the allow-list falls back to
       * the dashboard.
       */
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<UiState>) };
        if (!isLandingPath(merged.defaultLandingPath)) merged.defaultLandingPath = '/';
        // vendor#137: a device that persisted `appMode` keeps handing it back
        // here. Spreading it in would put a key on the store that no longer
        // exists in `UiState` — harmless to read, but it would survive every
        // future write through `partialize` and quietly outlive the feature.
        // Deleted explicitly rather than left to rot.
        delete (merged as Record<string, unknown>).appMode;
        return merged;
      },
    },
  ),
);
